import { useEffect, useState } from "react";
import { Plus, Trash2, X, Loader2, Building2, GraduationCap, Mail } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { api, formatApiError } from "@/lib/api";

const STATUSES = ["lead", "contacted", "proposal_sent", "contracted", "active_session"];
const STATUS_COLORS = {
  lead: "text-blue-400",
  contacted: "text-violet-400",
  proposal_sent: "text-amber-500",
  contracted: "text-emerald-400",
  active_session: "text-pink-400",
};

const EMPTY = { name: "", type: "adult", state: "", contact_name: "", contact_role: "", contact_email: "", status: "lead", follow_up_date: "", notes: "" };
const inputCls = "w-full bg-[#0D0E11] border border-white/10 focus:border-amber-500/60 rounded-lg px-3 py-2.5 text-sm text-[#F3F4F6] placeholder:text-[#6B7280] outline-none transition-colors";
const selectCls = "bg-[#0D0E11] border border-white/15 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:border-amber-500/60";

export default function OutreachPipeline({ onChanged }) {
  const [facilities, setFacilities] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [sendingFor, setSendingFor] = useState(null);

  const load = () => api.get("/facilities").then((r) => setFacilities(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/facilities", form);
      toast.success("Facility added to pipeline");
      setForm(EMPTY);
      setAdding(false);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/facilities/${id}`, { status });
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendTemplate = async (f, template) => {
    if (!template) return;
    setSendingFor(f.id);
    try {
      const { data } = await api.post(`/facilities/${f.id}/send-template`, { template });
      toast.success(`"${template.replace("_", " ")}" email sent to ${data.to}`);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingFor(null);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/facilities/${id}`);
      toast.success("Facility removed");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (facilities === null) return <div className="py-16 text-center font-mono-tactical text-xs tracking-[0.3em] text-amber-500 animate-pulse-amber">LOADING PIPELINE…</div>;

  return (
    <div data-testid="pipeline-panel">
      <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
        <p className="text-sm text-[#9CA3AF]">Every facility you've reached out to — track status, send template emails, and log follow-ups in one place.</p>
        <button onClick={() => setAdding(!adding)} className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-[#0D0E11] text-sm font-bold px-5 py-2.5 rounded-full transition-colors" data-testid="add-facility-btn">
          {adding ? <X size={15} /> : <Plus size={15} />} {adding ? "Cancel" : "Add Facility"}
        </button>
      </div>

      {adding && (
        <form onSubmit={create} className="bg-[#151821] border border-amber-500/30 rounded-xl p-6 mb-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="add-facility-form">
          <input required value={form.name} onChange={set("name")} placeholder="Facility name *" className={inputCls} data-testid="facility-name-input" />
          <select value={form.type} onChange={set("type")} className={inputCls} data-testid="facility-type-select">
            <option value="adult">Adult</option>
            <option value="juvenile">Juvenile</option>
          </select>
          <input value={form.state} onChange={set("state")} placeholder="State (e.g. TX)" maxLength={2} className={`${inputCls} uppercase`} data-testid="facility-state-input" />
          <input value={form.contact_name} onChange={set("contact_name")} placeholder="Contact name" className={inputCls} data-testid="facility-contact-name-input" />
          <input value={form.contact_role} onChange={set("contact_role")} placeholder="Contact role (Warden…)" className={inputCls} data-testid="facility-contact-role-input" />
          <input type="email" value={form.contact_email} onChange={set("contact_email")} placeholder="Contact email" className={inputCls} data-testid="facility-contact-email-input" />
          <input type="date" value={form.follow_up_date} onChange={set("follow_up_date")} className={inputCls} data-testid="facility-followup-input" />
          <input value={form.notes} onChange={set("notes")} placeholder="Notes" className={`${inputCls} lg:col-span-2`} data-testid="facility-notes-input" />
          <div className="lg:col-span-3">
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-[#0D0E11] text-sm font-bold px-6 py-2.5 rounded-full" data-testid="facility-save-btn">
              {saving && <Loader2 size={14} className="animate-spin" />} Save Facility
            </button>
          </div>
        </form>
      )}

      {facilities.length === 0 ? (
        <div className="py-16 text-center text-[#6B7280]" data-testid="pipeline-empty">No facilities yet. Add your first outreach target.</div>
      ) : (
        <div className="overflow-x-auto bg-[#151821] border border-white/10 rounded-xl" data-testid="pipeline-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 font-mono-tactical text-[10px] tracking-[0.2em] text-[#6B7280]">
                <th className="text-left px-5 py-3.5">FACILITY</th>
                <th className="text-left px-4 py-3.5">TYPE</th>
                <th className="text-left px-4 py-3.5">CONTACT</th>
                <th className="text-left px-4 py-3.5">FOLLOW-UP</th>
                <th className="text-left px-4 py-3.5">STATUS</th>
                <th className="text-left px-4 py-3.5">EMAIL</th>
                <th className="px-4 py-3.5"></th>
              </tr>
            </thead>
            <tbody>
              {facilities.map((f) => (
                <tr key={f.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`pipeline-row-${f.id}`}>
                  <td className="px-5 py-4">
                    <div className="font-semibold text-[#F3F4F6]">{f.name}</div>
                    {f.notes && <div className="text-xs text-[#6B7280] mt-0.5 max-w-xs truncate">{f.notes}</div>}
                  </td>
                  <td className="px-4 py-4">
                    <span className="inline-flex items-center gap-1.5 text-xs text-[#9CA3AF]">
                      {f.type === "juvenile" ? <GraduationCap size={13} className="text-amber-500" /> : <Building2 size={13} className="text-amber-500" />}
                      {f.type}
                    </span>
                    {f.state && <span className="ml-2 font-mono-tactical text-[10px] text-[#6B7280]">{f.state}</span>}
                  </td>
                  <td className="px-4 py-4 text-xs text-[#9CA3AF]">
                    {f.contact_name || "—"}
                    {f.contact_role && <div className="text-[#6B7280]">{f.contact_role}</div>}
                    {f.last_email_at && (
                      <div className="mt-1 font-mono-tactical text-[10px] text-amber-500/80" data-testid={`facility-last-email-${f.id}`}>
                        last: {f.last_email_template.replace("_", "-")} · {new Date(f.last_email_at).toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 font-mono-tactical text-xs text-[#9CA3AF]">{f.follow_up_date || "—"}</td>
                  <td className="px-4 py-4">
                    <select value={f.status} onChange={(e) => setStatus(f.id, e.target.value)} className={`${selectCls} ${STATUS_COLORS[f.status]}`} data-testid={`facility-status-${f.id}`}>
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{s.replace("_", " ")}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-1.5">
                      <Mail size={13} className="text-[#6B7280] shrink-0" />
                      <select
                        value=""
                        disabled={sendingFor === f.id}
                        onChange={(e) => sendTemplate(f, e.target.value)}
                        className={`${selectCls} text-[#9CA3AF] disabled:opacity-50`}
                        data-testid={`facility-outreach-${f.id}`}
                      >
                        <option value="">{sendingFor === f.id ? "Sending…" : "Send…"}</option>
                        <option value="intro">Intro</option>
                        <option value="proposal">Proposal</option>
                        <option value="follow_up">Follow-up</option>
                      </select>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <button onClick={() => remove(f.id)} className="text-[#6B7280] hover:text-red-400 transition-colors" data-testid={`facility-delete-${f.id}`} aria-label="Delete facility">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
